import React, { useEffect, useRef, useState } from "react";
import * as xlsx from "xlsx";
import { Modal } from "react-bootstrap";
import { set } from "react-hook-form";

export default function BySheet() {
  /*   All States
   ********************************************* */
  // Refs
  const fileInputRef = useRef(null);

  // Excel handling states
  const [loading, setLoading] = useState(false);
  const [wb, setWb] = useState(null);
  const [ws, setWs] = useState(null);
  const [showWorkSheetModal, setShowWorkSheetModal] = useState(false);
  const [columnLabels, setColumnLabels] = useState([]);

  const [jsonData, setJsonData] = useState([]);

  // User selection states
  const [groupColumn, setGroupColumn] = useState(null);
  const [sortDirections, setSortDirections] = useState({});
  const [sumColumns, setSumColumns] = useState([]);
  const [filteredColumns, setFilteredColumns] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [errors, setErrors] = useState([]);

  /*   All Functions
   ********************************************* */
  const loadingOn = () => setLoading(true);
  const loadingOff = () => setLoading(false);

  // Excel import function
  const readExcel = (e) => {
    let file = e.target.files[0];
    loadingOn();
    const promise = new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      if (!file) {
        loadingOff();
        return;
      }
      fileReader.readAsArrayBuffer(file);

      fileReader.onload = (e) => {
        const bufferArray = e.target.result;
        const wb = xlsx.read(bufferArray, { type: "buffer" });
        resolve(wb);
        loadingOff();
      };

      fileReader.onerror = (error) => {
        reject(error);
        loadingOff();
      };
    });

    promise.then((d) => {
      setWb(d);
    });
  };

  // Process data from selected sheet
  function getDataFromSelectedSheet(sheetName) {
    resetEverything();
    let selectedSheet = wb.Sheets[sheetName];
    const sheetData = xlsx.utils.sheet_to_json(selectedSheet, { header: 1 });

    const [firstRow, ...restRows] = sheetData;
    setColumnLabels(firstRow || []);

    // Convert to JSON format for processing
    const jsonDataFromSheet = restRows.map((row) => {
      const obj = {};
      firstRow.forEach((header, idx) => {
        obj[header] = row[idx];
      });
      return obj;
    });

    setJsonData(jsonDataFromSheet);

    setShowWorkSheetModal(false);
  }

  // Reset states
  const resetEverything = () => {
    setColumnLabels([]);
    setJsonData([]);
    setGroupColumn(null);
    setSortDirections({});
    setSumColumns([]);
    setProcessedData([]);
    setErrors([]);
  };

  // Clear all data and reset file input
  const handleClearButton = () => {
    resetEverything();
    setWb(null);
    setWs(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Toggle sort column selection
  const toggleSortColumn = (column) => {
    if (groupColumn.includes(column)) {
      setGroupColumn(groupColumn.filter((col) => col !== column));
      // Toggle direction if already in sort columns
      const newDirections = { ...sortDirections };
      if (newDirections[column]) {
        delete newDirections[column];
      }
      setSortDirections(newDirections);
    } else {
      setGroupColumn([...groupColumn, column]);
      // Set default direction to ascending
      setSortDirections({ ...sortDirections, [column]: "asc" });
    }
  };

  // Toggle sort direction
  const toggleSortDirection = (column) => {
    if (sortDirections[column] === "asc") {
      setSortDirections({ ...sortDirections, [column]: "desc" });
    } else {
      setSortDirections({ ...sortDirections, [column]: "asc" });
    }
  };

  // Toggle sum column selection
  const toggleSumColumn = (column) => {
    if (sumColumns.includes(column)) {
      setSumColumns(sumColumns.filter((col) => col !== column));
    } else {
      setSumColumns([...sumColumns, column]);
    }
  };

  // Process data based on user selections
  const processData = () => {
    setErrors([]);
    setProcessedData([]);
    const newErrors = [];

    if (sumColumns.length === 0) {
      newErrors.push("Please select at least one column for summing values");
    }

    // Validate numeric values in sum columns
    if (sumColumns.length > 0) {
      const nonNumericValues = [];

      jsonData.forEach((row, idx) => {
        sumColumns.forEach((col) => {
          const value = row[col];
          if (value !== undefined && value !== null && isNaN(Number(value))) {
            nonNumericValues.push(
              `Row ${idx + 1}, Column "${col}": "${value}" is not a number`
            );
          }
        });
      });

      if (nonNumericValues.length > 0) {
        // Limit error messages to avoid flooding
        newErrors.push(
          `Found non-numeric values in sum columns: ${nonNumericValues
            .slice(0, 5)
            .join(", ")}${
            nonNumericValues.length > 5
              ? ` and ${nonNumericValues.length - 5} more...`
              : ""
          }`
        );
      }
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    // Sort data
    let sortedData = [...jsonData];
    if (groupColumn) {
      sortedData.sort((a, b) => {
        const direction = sortDirections[groupColumn] === "desc" ? -1 : 1;

        if (a[groupColumn] < b[groupColumn]) return -1 * direction;
        if (a[groupColumn] > b[groupColumn]) return 1 * direction;
        return 0;
      });
    }

    // remove columns that are not in groupColumn or sumColumns

    // Sort filteredColumns according to columnLabels
    const _filteredColumns = [...groupColumn, ...sumColumns].reduce(
      (acc, item) => {
        const index = columnLabels.indexOf(item);
        if (index !== -1) {
          acc[index] = item;
          return acc;
        }
        return acc;
      },
      []
    );

    // Remove undefined values from _filteredColumns

    setFilteredColumns((prev) =>
      _filteredColumns.filter((col) => col !== undefined)
    );
    sortedData = sortedData.map((row) => {
      const newRow = {};
      _filteredColumns.forEach((col) => {
        newRow[col] = row[col];
      });
      return newRow;
    });

    // Group filteredColumns having same values and sum sumColumns for grouped rows
    const groupedData = sortedData.reduce((acc, row) => {
      let _processedData = acc || {};

      // get processedData key from current row named pKey
      const pKey = row[groupColumn];

      // check if processedData has pKey as key
      let pKeyExists = Object.keys(_processedData).includes(pKey);

      if (!pKeyExists) {
        // Add new key value of pKey : row
        _processedData[pKey] = row;
      } else {
        // Create a new row with adding value in sumColumns for pKey
        const _newRow = { [groupColumn]: pKey };
        const existingRow = _processedData[pKey];
        sumColumns?.forEach((col) => {
          const existingVal = Number(existingRow[col] || 0);
          const currRowVal = Number(row[col] || 0);
          _newRow[col] = existingVal + currRowVal;
        });
        _processedData[pKey] = _newRow;
      }
      return _processedData;
    }, {});

    // Convert back to array
    const result = Object.values(groupedData);

    setProcessedData(result);
  };

  // Export data to Excel
  const exportToExcel = () => {
    if (processedData.length === 0) return;

    // Create a new workbook
    const wb = xlsx.utils.book_new();

    // Convert processed data to worksheet format
    const wsData = [
      filteredColumns,
      ...processedData.map((row) => filteredColumns.map((col) => row[col])),
    ];

    const ws = xlsx.utils.aoa_to_sheet(wsData);

    // Add the worksheet to the workbook
    xlsx.utils.book_append_sheet(wb, ws, "Processed Data");

    // Generate Excel file and trigger download
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const timeStr = `${String(today.getHours()).padStart(2, "0")}${String(
      today.getMinutes()
    ).padStart(2, "0")}`;
    const fileName = `processed_data_${dateStr}_${timeStr}.xlsx`;
    xlsx.writeFile(wb, fileName);
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    if (wb) {
      resetEverything();
      setWs(wb.SheetNames);
      setShowWorkSheetModal(true);
    }
  }, [wb]);

  return (
    <div className="container-fluid ps-0 overflow-y-auto h-100">
      <h2 className="">Team Order Sheet</h2>

      {/* Layout Section  */}
      <div className="row container-fluid my-3">
        <div className={`col-md-9 px-0 `}>
          {/* Excel Import Section */}
          <div className="card shadow-sm mb-3">
            <div className="px-3 bg-primary text-white">
              <h5 className="">Import Excel Sheet</h5>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex gap-1 align-items-center">
                  <input
                    onChange={readExcel}
                    ref={fileInputRef}
                    type="file"
                    className="form-control"
                  />

                  {ws && (
                    <button
                      onClick={() => setShowWorkSheetModal(true)}
                      className="btn text-nowrap btn-info"
                    >
                      Change Sheet
                    </button>
                  )}

                  <button
                    onClick={handleClearButton}
                    className="btn btn-danger"
                  >
                    Clear
                  </button>
                </div>

                {columnLabels.length > 0 && (
                  <div>
                    <span className="badge fs-5 bg-success">
                      {columnLabels.length} columns and {jsonData.length} rows
                      loaded
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {errors.length > 0 && (
            <div className="alert alert-danger">
              <ul className="mb-0">
                {errors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          {/* Results Section */}
          {processedData.length > 0 && (
            <div className="card rounded-3 overflow-hidden shadow-sm">
              <div className=" px-3 bg-dark text-white d-flex align-items-center justify-content-between">
                <h5 className="">
                  Processed Data ({processedData.length} rows)
                </h5>
                <button
                  className="btn btn-success btn-sm my-2"
                  onClick={exportToExcel}
                >
                  Export to excel
                </button>
              </div>
              <div className="card-body">
                <div
                  className="table-responsive"
                  style={{ maxHeight: "55vh", overflowY: "auto" }}
                >
                  <table className="table table-striped table-bordered">
                    <thead>
                      <tr>
                        {filteredColumns?.map((column, i) => (
                          <th
                            key={i}
                            className={`
                      
                        ${
                          sumColumns.includes(column)
                            ? "bg-warning bg-opacity-25"
                            : ""
                        }
                      `}
                          >
                            {column}
                            {sumColumns.includes(column) && " (Summed)"}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {processedData.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {filteredColumns?.map((column, colIdx) => (
                            <td key={colIdx}>
                              {sumColumns.includes(column)
                                ? Number(row[column]).toLocaleString()
                                : row[column]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Configuration Section */}
        <div className="col-md-3">
          {columnLabels.length > 0 && (
            <div className=" ">
              {/* Sort Columns Selection */}
              <div className="">
                <div className="card mb-2 pb-2 ">
                  <div className=" px-3 bg-secondary text-white">
                    <h5 className="">Sort Columns</h5>
                  </div>
                  <div className="card-body" style={{ maxHeight: "150px" }}>
                    <p className="text-muted">
                      Select columns to sort by (in order of importance)
                    </p>

                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Group By Column
                      </label>

                      <div className="d-flex align-items-center justify-content-between ">
                        <select
                          className="form-select form-select-sm me-2"
                          value={groupColumn || ""}
                          onChange={(e) => {
                            setGroupColumn([e.target.value]);
                            setSortDirections({
                              [e.target.value]: "asc",
                            });
                          }}
                        >
                          <option value="">Select Column</option>
                          {columnLabels.map((col, idx) => (
                            <option key={idx} value={col}>
                              {col}
                            </option>
                          ))}
                        </select>
                        <div>
                          {groupColumn && (
                            <button
                              className={`btn btn-sm text-nowrap ${
                                sortDirections[groupColumn] === "asc"
                                  ? "btn-outline-success"
                                  : "btn-outline-danger"
                              }`}
                              onClick={() => toggleSortDirection(groupColumn)}
                            >
                              {sortDirections[groupColumn] === "asc"
                                ? "↑ ASC"
                                : "↓ DESC"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sum Columns Selection */}
              <div className="">
                <div className="card mb-2 pb-2">
                  <div className=" px-3 bg-warning">
                    <h5 className="">Sum Columns</h5>
                  </div>
                  <div
                    className="card-body"
                    style={{ maxHeight: "240px", overflowY: "auto" }}
                  >
                    <p className="text-muted">
                      Select numeric columns to sum when combining rows
                    </p>

                    {columnLabels.map((column, index) => (
                      <div key={index} className="form-check mb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`sum-${column}`}
                          checked={sumColumns.includes(column)}
                          onChange={() => toggleSumColumn(column)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`sum-${column}`}
                        >
                          {column}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Process Data */}
              <div>
                <button
                  className="btn btn-primary btn-lg w-100 mb-4"
                  onClick={processData}
                  disabled={sumColumns.length === 0}
                >
                  Process Data
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Worksheet Selection Modal */}
      <Modal
        show={showWorkSheetModal}
        onHide={() => setShowWorkSheetModal(false)}
      >
        <Modal.Body className="bg-light">
          <h3>Select WorkSheet</h3>
          <div className="my-3">
            {ws &&
              ws.map((item) => (
                <div
                  key={item}
                  className="bg-white shadow-sm rounded p-2 mb-2 d-flex align-items-center justify-content-between"
                >
                  <div>{item}</div>
                  <div>
                    <button
                      onClick={() => getDataFromSelectedSheet(item)}
                      className="btn btn-sm btn-success"
                    >
                      Select
                    </button>
                  </div>
                </div>
              ))}
          </div>
          <div className="text-end">
            <button
              onClick={() => setShowWorkSheetModal(false)}
              className="btn btn-sm btn-secondary"
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75"
          style={{ zIndex: 1050 }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
}
